import { Shield, FileText, Lock, Youtube, Gavel } from 'lucide-react';

const CONTACT_EMAIL = 'support@retroplaygroundtv.com';

export default function TermsPage() {
  return (
    <div className="page-content bg-[#FDFBEC] min-h-screen">
      <div className="pattern-bg" />
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 relative z-10">
        <div className="text-center mb-10">
          <FileText size={36} strokeWidth={2} className="text-[#4AC6FF] mx-auto mb-3" />
          <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-3" data-testid="terms-title">
            Terms & Privacy
          </h1>
          <p className="text-sm text-[#4B5563] font-['Nunito']">Effective February 2026</p>
        </div>

        <div className="space-y-6">
          {/* Content Disclaimer */}
          <div className="neo-card p-6 bg-white" data-testid="disclaimer-section">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={18} strokeWidth={2.5} className="text-[#FF3860]" />
              <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">Content Disclaimer</h2>
            </div>
            <div className="text-sm text-[#4B5563] font-['Nunito'] space-y-3 leading-relaxed">
              <p>
                This website curates publicly available videos from YouTube. <strong>We do not host, own, or claim ownership of any video content displayed on this site.</strong> All videos are embedded directly from YouTube using their official embed player. All rights to the shows, characters, and related content belong to their respective owners and copyright holders.
              </p>
              <p>
                Retro Playground TV is an independent curation service and is not affiliated with, endorsed by, or connected to YouTube, Google, Nickelodeon, Disney, Cartoon Network, PBS, or any other network or content creator.
              </p>
              <p>
                If you are a rights holder and believe your content should not be featured on this site, please contact us at{' '}
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Rights%20Holder%20Content%20Removal%20Request`}
                  className="font-bold text-[#FF3860] underline break-all hover:text-[#0A0A0A]"
                  data-testid="terms-rights-holder-email"
                >
                  {CONTACT_EMAIL}
                </a>{' '}
                and we will promptly remove it. See our <strong>DMCA / Rights Holder Notice</strong> below.
              </p>
            </div>
          </div>

          {/* Terms of Use */}
          <div className="neo-card p-6 bg-white" data-testid="terms-section">
            <div className="flex items-center gap-2 mb-3">
              <Gavel size={18} strokeWidth={2.5} className="text-[#0A0A0A]" />
              <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">Terms of Use</h2>
            </div>
            <div className="text-sm text-[#4B5563] font-['Nunito'] space-y-3 leading-relaxed">
              <p>
                By using Retro Playground TV (&quot;the Site&quot;), you agree to the following:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>All video content is embedded from YouTube and subject to YouTube&apos;s Terms of Service.</li>
                <li>We do not download, re-upload, or redistribute any video content.</li>
                <li>This Site is intended for family use. Parents and guardians are responsible for supervising children&apos;s viewing.</li>
                <li>Account information is stored securely. We do not sell or share personal data with third parties.</li>
                <li>Donations are entirely voluntary and do not grant any additional access. All content remains free for everyone. Donations are not tax-deductible, as Retro Playground TV is not a registered 501(c)(3) nonprofit. Donations support site hosting and maintenance only.</li>
                <li>We reserve the right to modify, add, or remove content and features at any time.</li>
                <li>You must be 18 or older to create an account. Kid profiles are created and managed by the account-holding parent only.</li>
                <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
              </ul>
              <p>
                The Site is provided &quot;as is,&quot; without warranties of any kind. We do not control YouTube&apos;s servers and cannot guarantee any video will remain available. To the fullest extent permitted by law, Retro Playground TV is not liable for indirect or consequential damages arising from use of the Site. These Terms are governed by the laws of the <strong>State of South Carolina</strong>.
              </p>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="neo-card p-6 bg-white" data-testid="privacy-section">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={18} strokeWidth={2.5} className="text-[#4AC6FF]" />
              <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">Privacy Policy</h2>
            </div>
            <div className="text-sm text-[#4B5563] font-['Nunito'] space-y-3 leading-relaxed">
              <p>
                <strong>What we collect:</strong> Email address and name for the account holder; favorites and watch history; and, for kid profiles, a nickname chosen by the parent (not the child&apos;s real name) plus an avatar color.
              </p>
              <p>
                <strong>What we don&apos;t collect:</strong> We have zero access to your YouTube or Google account. We do not collect a child&apos;s real name, birthdate, photo, or location.
              </p>
              <p>
                <strong>How we use your data:</strong> Solely to provide account features. We do not sell, share, or monetize your personal information.
              </p>
              <p>
                <strong>Cookies:</strong> Essential cookies only, for keeping you logged in. No tracking or advertising cookies.
              </p>
              <p>
                <strong>Children&apos;s privacy:</strong> Kid profiles store only a parent-chosen nickname and avatar color — nothing that identifies the actual child. Parents can edit or delete a kid profile anytime from account settings.
              </p>
              <p>
                <strong>Donations:</strong> Processed securely by PayPal. We do not store payment card information. Donations are not tax-deductible.
              </p>
            </div>
          </div>

          {/* YouTube Embed Notice */}
          <div className="neo-card p-6 bg-white" data-testid="youtube-embed-section">
            <div className="flex items-center gap-2 mb-3">
              <Youtube size={18} strokeWidth={2.5} className="text-[#FF3860]" />
              <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">YouTube Embed Notice</h2>
            </div>
            <div className="text-sm text-[#4B5563] font-['Nunito'] space-y-3 leading-relaxed">
              <p>
                All videos are displayed using YouTube&apos;s official embed player and play from YouTube&apos;s servers, not ours.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Videos are subject to YouTube&apos;s terms, ads policy, and availability.</li>
                <li>YouTube Premium users see embedded videos ad-free automatically.</li>
                <li>We cannot control if a video is removed by its uploader.</li>
                <li>Your viewing is governed by Google&apos;s Privacy Policy when interacting with the player.</li>
              </ul>
            </div>
          </div>

          {/* DMCA / Rights Holder Notice */}
          <div className="neo-card p-6 bg-white" data-testid="dmca-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={18} strokeWidth={2.5} className="text-[#FF6B00]" />
              <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">DMCA / Rights Holder Notice</h2>
            </div>
            <div className="text-sm text-[#4B5563] font-['Nunito'] space-y-3 leading-relaxed">
              <p>
                To request removal of a video from curation on this Site, email{' '}
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=DMCA%20Removal%20Request`}
                  className="font-bold text-[#FF3860] underline break-all hover:text-[#0A0A0A]"
                  data-testid="terms-dmca-email"
                >
                  {CONTACT_EMAIL}
                </a>{' '}
                with:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>A description of the work,</li>
                <li>The URL on our Site,</li>
                <li>Your contact information, and</li>
                <li>A good-faith statement that use is unauthorized.</li>
              </ul>
              <p>
                We respond within <strong>5 business days</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
